// The tastings a person has left open, in one list wherever they look (Journal, Samen, the live tab
// of Sessions): the ones on this device, another device's copy when they turned that on, and the
// flights and sessions the edge says they are in. A tap takes them to the pager at the page and wine
// they left (the row's actions are its menu and swipe, so the tap has keyboard and menu twins).
import { useEffect, useRef } from "react";
import { useConfirm } from "./ConfirmSheet";
import { useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import { useSession } from "../lib/social/auth";
import { clearNotices, useDraftNotices } from "../lib/tasting/draftNotices";
import { closeUnfinished, discardUnfinished, resumeRoute } from "../lib/tasting/resume";
import type { CloseTarget, UnfinishedItem } from "../lib/tasting/unfinished";
import { useUnfinished } from "../lib/tasting/useUnfinished";
import { Banner, Card, Column, Icon, ListRow, Section, Status, type RowAction } from "../ui";

/** Puts the taster back in front of the tasting a row stands for. */
function open(item: UnfinishedItem): void {
  const route = resumeRoute(item.target);
  if (route !== undefined) navigate(route);
}

export interface UnfinishedListProps {
  /** Leaves out the heading, for a screen that already says what the list is. */
  bare?: boolean;
}

export function UnfinishedList({ bare = false }: UnfinishedListProps): React.JSX.Element | null {
  const { t, locale } = useT();
  const auth = useSession();
  const userId = auth.status === "signedIn" ? auth.user.id : undefined;
  const { items, refresh } = useUnfinished();
  const notices = useDraftNotices();
  const { ask, confirmation } = useConfirm();

  // Told once: the notices on screen are forgotten when the list goes away. Keyed to the unmount
  // alone, because clearing on every change re-emits and re-renders the list without end.
  const shown = useRef(notices);
  shown.current = notices;
  useEffect(
    () => () => {
      clearNotices(shown.current);
    },
    [],
  );

  if (items.length === 0 && notices.length === 0) return null;

  const when = (stamp: string): string =>
    new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(stamp),
    );

  const subtitleOf = (item: UnfinishedItem): string =>
    [
      item.wineNo !== undefined && item.wineTotal > 0
        ? t("continue.row.wine", { n: item.wineNo, total: item.wineTotal })
        : undefined,
      item.depth === undefined
        ? undefined
        : t(item.depth === "quick" ? "tasting.flow.depth.quick" : "tasting.flow.depth.full"),
      t(item.shared ? "continue.row.shared" : "continue.row.solo"),
      item.startedAt === undefined
        ? undefined
        : t("continue.row.started", { when: when(item.startedAt) }),
      item.waiting ? t("continue.row.waiting") : undefined,
      item.fromOtherDevice ? t("continue.row.otherDevice") : undefined,
    ]
      .filter((part) => part !== undefined)
      .join(" · ");

  const closeActionOf = (item: UnfinishedItem, closing: CloseTarget): RowAction => {
    const ending = closing.kind === "end_session" || closing.kind === "end_flight";
    return {
      id: "close",
      label: t(ending ? "continue.list.end" : "continue.list.leave"),
      icon: ending ? "flag" : "x",
      tone: "danger",
      onSelect: () => {
        ask({
          title: t(ending ? "continue.end.title" : "continue.leave.title", { title: item.title }),
          body: t(ending ? "continue.end.body" : "continue.leave.body"),
          confirmLabel: t(ending ? "continue.list.end" : "continue.list.leave"),
          onConfirm: async () => {
            await closeUnfinished(closing);
            refresh();
          },
        });
      },
    };
  };

  const actionsOf = (
    item: UnfinishedItem,
  ): { resume: RowAction; discard: RowAction | undefined; close: RowAction | undefined } => ({
    resume: {
      id: "resume",
      label: t("continue.list.resume"),
      icon: "enter",
      onSelect: () => {
        open(item);
      },
    },
    discard: item.discardable
      ? {
          id: "discard",
          label: t("continue.list.discard"),
          icon: "trash",
          tone: "danger",
          onSelect: () => {
            ask({
              title: t("continue.discard.title", { title: item.title }),
              body: t(item.shared ? "continue.discard.shared.body" : "continue.discard.body"),
              confirmLabel: t("continue.list.discard"),
              onConfirm: async () => {
                await discardUnfinished(item.target, userId);
                refresh();
              },
            });
          },
        }
      : undefined,
    close: item.closing === undefined ? undefined : closeActionOf(item, item.closing),
  });

  const list = (
    <Column gap="3" data-testid="unfinished-list">
      {notices.map((notice) => (
        <Banner
          key={notice.runId}
          tone="info"
          message={t("continue.list.conflict", { title: notice.title })}
          data-testid="unfinished-conflict"
        />
      ))}
      {items.length > 0 && (
        <Card flush>
          {items.map((item) => {
            const { resume, discard, close } = actionsOf(item);
            const danger = discard ?? close;
            return (
              <ListRow
                key={item.id}
                testId={`unfinished-${item.id}`}
                title={item.title}
                subtitle={subtitleOf(item)}
                leading={<Icon name={item.shared ? "users" : "play"} size={20} />}
                trailing={
                  <>
                    <Status tone="accent">{t("continue.list.resume")}</Status>
                    <Icon name="chevron-right" size={18} />
                  </>
                }
                onPress={resume.onSelect}
                actions={danger === undefined ? [resume] : [resume, danger]}
                swipeEnd={danger}
              />
            );
          })}
        </Card>
      )}
      {confirmation}
    </Column>
  );
  return bare ? (
    list
  ) : (
    <Section title={t("continue.list.title")} data-testid="unfinished">
      {list}
    </Section>
  );
}
